/**
 * @architecture-header 1
 * @layer module-ui
 * @domain settings-invitations
 * @responsibility Compose independently tuned desktop and mobile SET-F06 Invitations pane surfaces
 *   from backend-authoritative invitation-list props.
 * @state-owner React transient create-form and just-revealed-token state; parent owns server state
 * @allowed-imports React; shared translator; @vispo/contracts types; react-hook-form; zod
 * @forbidden direct API client; fabricated authorization; email-sending adapters
 * @public-api InvitationsSettingsSurface
 * @side-effects invokes parent commands only
 * @tests InvitationsSettingsSurface.test.tsx
 */

import { zodResolver } from '@hookform/resolvers/zod';
import type { OrganizationInvitation, OrganizationInvitationRole } from '@vispo/contracts';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { useTranslator } from '../../../shared/model/use-workspace';
import './settings-invitations.css';

const ROLES: readonly OrganizationInvitationRole[] = ['admin', 'staff', 'member'];
const STATUSES = ['pending', 'accepted', 'declined', 'revoked', 'expired', 'all'] as const;
const EMPTY_INVITATIONS: readonly OrganizationInvitation[] = Object.freeze([]);

export interface InvitationsSettingsSurfaceProps {
  canManage: boolean;
  commandError?: unknown;
  invitations: readonly OrganizationInvitation[] | null;
  errorCode?: string | null;
  loading?: boolean;
  mobile: boolean;
  onCreate?(input: {
    expiresAt: string;
    invitedRole: OrganizationInvitationRole;
    recipientEmail: string;
  }): void;
  onRetry?(): void;
  onRevoke?(invitation: OrganizationInvitation): void;
  onResend?(invitation: OrganizationInvitation): void;
  onStatusChange?(status: (typeof STATUSES)[number]): void;
  revealedToken?: { invitationId: string; token: string } | null;
  saving?: boolean;
  status: (typeof STATUSES)[number];
}

function defaultExpiry(): string {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date.toISOString().slice(0, 10);
}

function CreateInvitationForm({
  onCreate,
  saving,
}: Pick<InvitationsSettingsSurfaceProps, 'onCreate' | 'saving'>) {
  const t = useTranslator();
  const form = useForm<{
    expiresAt: string;
    invitedRole: 'admin' | 'staff' | 'member';
    recipientEmail: string;
  }>({
    resolver: zodResolver(
      z.object({
        recipientEmail: z.string().trim().min(1),
        invitedRole: z.enum(['admin', 'staff', 'member']),
        expiresAt: z.string().trim().min(1),
      }),
    ),
    defaultValues: { recipientEmail: '', invitedRole: 'member', expiresAt: defaultExpiry() },
  });
  return (
    <form
      className="invitations-settings-pane__create-form"
      onSubmit={form.handleSubmit((value) => {
        onCreate?.({
          recipientEmail: value.recipientEmail,
          invitedRole: value.invitedRole,
          expiresAt: new Date(`${value.expiresAt}T00:00:00Z`).toISOString(),
        });
        form.reset({ recipientEmail: '', invitedRole: 'member', expiresAt: defaultExpiry() });
      })}
    >
      <label>
        <span>{t('Email')}</span>
        <input aria-label={t('Email')} type="email" {...form.register('recipientEmail')} />
      </label>
      <label>
        <span>{t('角色')}</span>
        <select aria-label={t('邀請角色')} {...form.register('invitedRole')}>
          {ROLES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span>{t('到期日')}</span>
        <input aria-label={t('到期日')} type="date" {...form.register('expiresAt')} />
      </label>
      <button
        className="bt pri"
        disabled={saving || !form.watch('recipientEmail').trim()}
        type="submit"
      >
        {t('送出邀請')}
      </button>
    </form>
  );
}

function InvitationRows({
  canManage,
  invitations,
  onResend,
  onRevoke,
  saving,
}: Pick<InvitationsSettingsSurfaceProps, 'canManage' | 'onResend' | 'onRevoke' | 'saving'> & {
  invitations: readonly OrganizationInvitation[];
}) {
  const t = useTranslator();
  return (
    <ul className="invitations-settings-pane__list">
      {invitations.map((invitation) => (
        <li className="invitations-settings-pane__row" key={invitation.id}>
          <div className="invitations-settings-pane__identity">
            <strong>{invitation.recipientEmail}</strong>
            <code>{invitation.invitedRole}</code>
          </div>
          <span
            className={`invitations-settings-pane__badge invitations-settings-pane__badge--${invitation.status}`}
          >
            {invitation.status}
          </span>
          <span className="invitations-settings-pane__expiry">
            {new Date(invitation.expiresAt).toLocaleDateString()}
          </span>
          {canManage && invitation.status === 'pending' ? (
            <div className="invitations-settings-pane__actions">
              <button
                className="bt sm"
                disabled={saving}
                type="button"
                onClick={() => onResend?.(invitation)}
              >
                {t('重寄')}
              </button>
              <button
                className="bt sm"
                disabled={saving}
                type="button"
                onClick={() => onRevoke?.(invitation)}
              >
                {t('撤銷')}
              </button>
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function InvitationsSettingsSurface(props: InvitationsSettingsSurfaceProps) {
  const t = useTranslator();
  // Keyed by the token string itself, not invitationId and not a bare boolean: a resend rotates
  // the SAME invitation's token, so keying by id would stay "dismissed" across that new, never-
  // before-shown token too. Every mint (create or resend) produces a distinct token value.
  const [dismissedToken, setDismissedToken] = useState<string | null>(null);
  const revealedToken =
    props.revealedToken && props.revealedToken.token !== dismissedToken
      ? props.revealedToken
      : null;

  if (props.loading) {
    return (
      <section
        aria-label={t('Invitations')}
        className={`invitations-settings-pane${props.mobile ? ' invitations-settings-pane--mobile' : ' invitations-settings-pane--desktop'}`}
      >
        <VispoLoading className="invitations-settings-pane__loading" label={t('正在載入邀請')} />
      </section>
    );
  }

  if (props.errorCode === 'ORGANIZATION_INVITATION_ADMIN_REQUIRED') {
    return (
      <section
        aria-label={t('Invitations')}
        className={`invitations-settings-pane${props.mobile ? ' invitations-settings-pane--mobile' : ' invitations-settings-pane--desktop'}`}
      >
        <div className="invitations-settings-pane__blocked" role="alert">
          <strong>{t('無法查看組織邀請')}</strong>
          <span>{t('只有此組織的 owner 或 admin 可以管理邀請。')}</span>
        </div>
      </section>
    );
  }

  if (props.errorCode && !props.commandError) {
    return (
      <section
        aria-label={t('Invitations')}
        className={`invitations-settings-pane${props.mobile ? ' invitations-settings-pane--mobile' : ' invitations-settings-pane--desktop'}`}
      >
        <div className="invitations-settings-pane__blocked" role="alert">
          <strong>{t('組織邀請暫時無法載入')}</strong>
          <button className="bt sm" type="button" onClick={props.onRetry}>
            {t('重試')}
          </button>
        </div>
      </section>
    );
  }

  const invitations = props.invitations ?? EMPTY_INVITATIONS;

  return (
    <section
      aria-label={t('Invitations')}
      className={`invitations-settings-pane${props.mobile ? ' invitations-settings-pane--mobile' : ' invitations-settings-pane--desktop'}`}
    >
      {props.saving ? <div role="status">{t('正在處理…')}</div> : null}
      {props.commandError ? (
        <div role="alert">
          {props.errorCode === 'ORGANIZATION_INVITATION_STATE_CONFLICT'
            ? t('資料已更新，請重新整理後再試。')
            : props.errorCode === 'ORGANIZATION_INVITATION_ACTIVE'
              ? t('此使用者已經有一筆待處理的邀請。')
              : t('操作失敗，請重新整理後再試。')}
        </div>
      ) : null}

      {revealedToken ? (
        <div className="invitations-settings-pane__token-reveal" role="status">
          <strong>{t('邀請連結（僅顯示一次）')}</strong>
          <code>{`${window.location.origin}/organization-invitations/${revealedToken.token}`}</code>
          <button
            className="bt sm"
            type="button"
            onClick={() => {
              void navigator.clipboard?.writeText(
                `${window.location.origin}/organization-invitations/${revealedToken.token}`,
              );
            }}
          >
            {t('複製')}
          </button>
          <button
            className="bt sm"
            type="button"
            onClick={() => setDismissedToken(revealedToken.token)}
          >
            {t('關閉')}
          </button>
          <p className="invitations-settings-pane__note">
            {t('此連結不會再次顯示，請立即複製並分享給受邀者。')}
          </p>
        </div>
      ) : null}

      <div className="invitations-settings-pane__toolbar">
        <label>
          <span className="sr-only">{t('狀態篩選')}</span>
          <select
            aria-label={t('狀態篩選')}
            value={props.status}
            onChange={(event) =>
              props.onStatusChange?.(event.target.value as (typeof STATUSES)[number])
            }
          >
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
      </div>

      {props.canManage ? (
        <CreateInvitationForm onCreate={props.onCreate} saving={props.saving} />
      ) : null}

      {invitations.length ? (
        <InvitationRows
          canManage={props.canManage}
          invitations={invitations}
          onResend={props.onResend}
          onRevoke={props.onRevoke}
          saving={props.saving}
        />
      ) : (
        <div className="invitations-settings-pane__empty">{t('尚無邀請。')}</div>
      )}

      {props.mobile ? (
        <p className="invitations-settings-pane__note">
          {t('邀請連結需由管理者自行分享；Vispo 不會寄送邀請信件。')}
        </p>
      ) : null}
    </section>
  );
}
